// Collapsable UI-only (no logic) component to display all patient history items

import {
  Card,
  CardBody,
  type CardColors,
  CardDetails,
  CardSummary,
} from "$components/card";
import { getCardColor } from "$components/card/utils";
import {
  DebugMode,
  useDevTools,
} from "$global-providers/dev-tools-provider/provider";
import {
  makeStyles,
  styled,
  Typography,
  type TypographyProps,
} from "@material-ui/core";

// The footer sits outside the collapsible body and full-bleeds to the card
// edges (the expandable Card has no padding of its own); footer components own
// their internal padding.
const FooterWrapper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

export const PatientHistoryCard: FCwC<{
  color: CardColors;
  HeaderComponent: FC;
  FooterComponent?: FC;
  PostFooterSection?: React.ReactNode;
  hideExpandIcon?: boolean;
  expanded?: boolean;
  defaultExpanded?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}> = ({
  color,
  HeaderComponent,
  FooterComponent = null,
  PostFooterSection = null,
  hideExpandIcon = false,
  expanded,
  defaultExpanded,
  disabled,
  className,
  children,
  ...restProps
}) => {
  return (
    <Card
      color={color}
      expandable
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      disabled={disabled}
      hideExpandIcon={hideExpandIcon}
      className={className}
      {...restProps}
    >
      <CardBody>
        <CardSummary>
          <HeaderComponent />
        </CardSummary>
        <CardDetails>{children}</CardDetails>
      </CardBody>
      {FooterComponent && (
        <FooterWrapper>
          <FooterComponent />
        </FooterWrapper>
      )}
      {PostFooterSection}
    </Card>
  );
};

export type CardSubtitleProps = {
  color: CardColors;
} & Omit<TypographyProps, "color">;

const useStyles = makeStyles((theme) => ({
  subtitle: ({ color }: { color: CardColors }) => ({
    fontSize: 12,
    color: getCardColor(theme, color),
    minHeight: 0,
  }),
}));

export const CardSubtitle = ({
  children,
  color,
  ...props
}: CardSubtitleProps) => {
  const classes = useStyles({ color });
  return (
    <Typography className={classes.subtitle} {...props}>
      {children}
    </Typography>
  );
};

const HeaderContainer = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexGrow: 1,
}));

const TitlesContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
}));

const Row = styled("div")(() => ({
  display: "flex",
  gap: "4px",
}));

export const CardHeader: FCwC<{
  color: CardColors;
  title?: string;
  category?: string;
  icon?: React.ReactNode;
  extras?: React.ReactNode;
  id?: string;
}> = ({ color, title, category, icon, extras, id }) => {
  const { debugMode } = useDevTools();

  return (
    <HeaderContainer>
      <TitlesContainer>
        {category && (
          <CardSubtitle color={color}>
            {category}
            {debugMode !== DebugMode.None && id && (
              <span
                style={{
                  color: "gray",
                  fontSize: "0.8em",
                  marginLeft: "8px",
                }}
              >
                ID: {id}
              </span>
            )}
          </CardSubtitle>
        )}
        {(icon || title) && (
          <Row>
            {icon}
            {title && (
              <Typography variant="h5" style={{ flexGrow: 1 }}>
                <strong>{title}</strong>
              </Typography>
            )}
          </Row>
        )}
      </TitlesContainer>
      {extras && (
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {extras}
        </div>
      )}
    </HeaderContainer>
  );
};
